import { useCallback, useState } from 'react';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import {
  TravelerProfile,
  TravelerProfileDetailsUpdate,
} from '@marageti/z4-sdk/lib/people';

function toDetailsUpdate(p: TravelerProfile): TravelerProfileDetailsUpdate {
  return {
    id: p.id,
    notes: p.notes ?? '',
    address: p.address,
    desiredDestinations: p.desiredDestinations ?? [],
    travelPreferences: p.travelPreferences ?? {},
    personalDetails: p.personalDetails,
    significantDates: p.significantDates ?? [],
    loyaltyPrograms: p.loyaltyPrograms ?? '',
    giftsReceived: p.giftsReceived ?? '',
    householdDetails: p.householdDetails ?? '',
    countriesVisited: p.countriesVisited ?? [],
    travelerNotes: p.travelerNotes ?? [],
  };
}

export default function useTravelerProfile(travelerId: string | undefined) {
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: travelerProfile,
    mutate: mutateTravelerProfile,
    isLoading,
    error,
  } = useFetch<TravelerProfile>(
    apiClient.peopleClient.getTravelerProfile,
    [travelerId],
    !!travelerId,
  );

  const updateTravelerProfile = useCallback(
    async (
      updates: Partial<TravelerProfileDetailsUpdate>,
      errorMessage = 'Could not save traveler profile.',
      successMessage = 'Traveler profile updated.',
    ) => {
      if (!travelerProfile) return undefined;
      setIsSaving(true);
      try {
        const updated = await apiClient.peopleClient.putTravelerProfile(
          travelerProfile.id,
          { ...toDetailsUpdate(travelerProfile), ...updates },
        );
        await mutateTravelerProfile(updated, { revalidate: false });
        enqueueSnackbar(successMessage, { variant: 'success' });
        return updated;
      } catch {
        enqueueSnackbar(errorMessage, { variant: 'default' });
        return undefined;
      } finally {
        setIsSaving(false);
      }
    },
    [travelerProfile, apiClient, mutateTravelerProfile, enqueueSnackbar],
  );

  return {
    travelerProfile,
    mutateTravelerProfile,
    updateTravelerProfile,
    isLoading,
    isSaving,
    error,
  };
}
